// challenge-step-check — the pure copy behind the step view's "Check this step"
// (step-checks, Trello IeSYCMgQ). No DOM and no imports, so every line is
// host-testable (test/editor/challenge-step-check.test.js).
//
//   stepCheckMessage(results, step) → { tone: 'pass' | 'fail', lines: [String] } | null
//   stepCheckSummary(message)       → the one-line foot summary of a message ('' for none)
//
// `results` is a list of step-check results in three families
// (step-checks-prompts-functions, Trello EiSNIhrA):
//   - rows      `gradeStepCheck(checkpoint, consoleRunData, step)` (no `family`)
//   - prompts   `gradePromptStepCheck(...)`   (`family: 'prompt'`)
//   - functions `gradeFunctionStepCheck(...)` (`family: 'function'`)
// The failure with the EARLIEST `rowStep` supplies the message (ties: list order);
// `null` when every result is skipped (nothing tagged <= step — the button is never
// shown then).
//
// ⚠️ Never the golden transcript, never the reference source: only the first
// differing line. The caller renders every line with `textContent`.

export var STEP_CHECK_TIMEOUT_LINE = 'That took too long. Try Check this step again.';
export var STEP_CHECK_RUNNER_ERROR_LINE = 'Your program could not start. Press Run to see what went wrong.';

// The stdin value a sentence names. A normal row: its LAST value (the one the rows
// of a table differ by, e.g. 1.9's codes after the shared name/level answers). An
// invalid row: the value just before the last, because the last one is the
// recovery value the program should reach after asking again.
function rowValue(row, invalid) {
  var stdin = (row && Array.isArray(row.stdin)) ? row.stdin : [];
  if (stdin.length === 0) { return 'no input'; }
  var i = (invalid && stdin.length >= 2) ? stdin.length - 2 : stdin.length - 1;
  return String(stdin[i]);
}

function upperFirst(s) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

// A sentence ending in `line` gets a full stop unless the line already ends in one.
function endSentence(s) {
  return /[.!?]$/.test(s) ? s : s + '.';
}

function failReason(result) {
  var reason = result.reason || {};
  var v = rowValue(result.row, result.row && result.row.tag === 'invalid');
  switch (reason.kind) {
    case 'line':
      // Lines are shown as authored / as printed, so a line may already end a sentence.
      return 'With ' + v + ' your program printed ' + endSentence(reason.actualLine) +
        ' It should print ' + endSentence(reason.expectedLine);
    case 'empty':
      return 'With ' + v + ' your program printed nothing. It should print ' + endSentence(reason.expectedLine);
    case 'short':
      return 'With ' + v + ' your program stopped before printing ' + endSentence(reason.expectedLine);
    case 'extra':
      return 'With ' + v + ' your program printed ' + endSentence(reason.actualLine) + ' It should not print that.';
    case 'crash':
      return 'With ' + v + ' your program crashed.';
    case 'no-recovery':
      return 'With ' + v + ' your program stopped instead of asking again.';
    default:
      // No step-check copy for this cause (no run, too many inputs read, a
      // full-transcript difference): the grader's own non-leaky sentence.
      return String(result.diff || ('With ' + v + ' your program did not pass.'));
  }
}

// A prompt as shown in a sentence: as authored, without its edge spaces (a stored
// prompt usually ends in one, "Type the 4-digit door code: ").
function shownPrompt(text) {
  return String(text == null ? '' : text).replace(/^ +| +$/g, '');
}

// Prompt fail (B4, A3): generic "before it reads the input", plus what it asked
// instead when the probe reached a non-empty prompt line.
function promptReason(result) {
  var s = 'Your program should ask ' + shownPrompt(result.prompt) + ' before it reads the input.';
  var asked = shownPrompt(result.asked);
  if (asked !== '') { s += ' It asked ' + asked + ' instead.'; }
  return s;
}

// Function fail (A7): the full Check's own sentence from compareFunctionIO. Its inline
// code backticks are dropped because step-check lines are rendered with
// `textContent` (they would show literally); the wording is otherwise unchanged.
function functionReason(result) {
  var d = String(result.diff || '').replace(/`/g, '');
  return d || 'Your function did not pass.';
}

function failMessage(result, step) {
  var family = result.family;
  var reason = family === 'prompt' ? promptReason(result)
    : family === 'function' ? functionReason(result)
    : failReason(result);
  var rowStep = result.rowStep;
  var parts = reason.split('\n'); // a printed-text failure carries its Expected / Printed lines
  var lead;
  if (typeof rowStep === 'number' && rowStep < step) {
    // A function sentence starts with the call ("double(5) should be…"): never capitalise it.
    parts[0] = family === 'function' ? parts[0] : upperFirst(parts[0]);
    lead = 'Step ' + rowStep + "'s test is failing again: ";
  } else {
    lead = 'Not yet. ';
  }
  parts[0] = lead + parts[0];
  return { tone: 'fail', lines: parts };
}

// One line from the rows tagged EXACTLY `step`: rows sharing a summary line (the
// first expected result line not common to every row of the checkpoint, as authored)
// → "<v1> and <v2> both print <line>." (one row: "<v> prints <line>."), an invalid
// row → "<v> does not crash it."; at most 2 clauses, otherwise (or a group of 3+, or
// a row expecting no output) "<n> tests passed."
function passSummary(rows) {
  var n = rows.length;
  if (n === 0) { return null; }
  var counted = n + (n === 1 ? ' test passed.' : ' tests passed.');
  var groups = [];
  var byLine = {};
  for (var i = 0; i < rows.length; i++) {
    var r = rows[i];
    if (r.invalid) {
      groups.push({ invalid: true, values: [rowValue(r.row, true)] });
      continue;
    }
    // `summaryLine` (gradeStepCheck): the row's first expected line NOT shared by
    // every row of its checkpoint, as authored; never the normalised compare form.
    var line = typeof r.summaryLine === 'string' ? r.summaryLine : '';
    if (line === '') { return counted; }
    var key = 'k:' + line;
    if (!byLine[key]) {
      byLine[key] = { invalid: false, line: line, values: [] };
      groups.push(byLine[key]);
    }
    byLine[key].values.push(rowValue(r.row, false));
  }
  if (groups.length > 2) { return counted; }
  var clauses = [];
  for (var g = 0; g < groups.length; g++) {
    var grp = groups[g];
    if (grp.invalid) { clauses.push(grp.values[0] + ' does not crash it.'); continue; }
    if (grp.values.length === 1) { clauses.push(grp.values[0] + ' prints ' + endSentence(grp.line)); continue; }
    if (grp.values.length === 2) {
      clauses.push(grp.values[0] + ' and ' + grp.values[1] + ' both print ' + endSentence(grp.line));
      continue;
    }
    return counted;
  }
  return clauses.join(' ');
}

// The ONE line the pinned foot shows for a result (step-area fix, after fix round 1): the full
// message lives in the step area, so the foot never grows past a single short line.
//   pass                → its first line ("Step 3 works.")
//   an earlier step     → "Step 2's test is failing again."
//   timeout / no start  → the first sentence of that line
//   any other fail      → "Not yet. See what went wrong above."
// '' for no message (the foot line is then empty and takes no room).
export var STEP_CHECK_SUMMARY_FAIL = 'Not yet. See what went wrong above.';
export function stepCheckSummary(msg) {
  if (!msg || !Array.isArray(msg.lines) || !msg.lines.length) { return ''; }
  var first = String(msg.lines[0]);
  if (msg.tone === 'pass') { return first; }
  var again = /^(Step \d+'s test is failing again)/.exec(first);
  if (again) { return again[1] + '.'; }
  if (first === STEP_CHECK_TIMEOUT_LINE) { return 'That took too long.'; }
  if (first === STEP_CHECK_RUNNER_ERROR_LINE) { return 'Your program could not start.'; }
  return STEP_CHECK_SUMMARY_FAIL;
}

function stepOf(result) {
  return typeof result.rowStep === 'number' ? result.rowStep : Infinity;
}

// The pass line of a prompt: "It asks <prompt>" with a full stop unless the prompt
// already ends a sentence or with a colon ("It asks Type the 4-digit door code:").
function promptPassLine(text) {
  var p = shownPrompt(text);
  return 'It asks ' + (/[.!?:]$/.test(p) ? p : p + '.');
}

export function stepCheckMessage(results, step) {
  var list = Array.isArray(results) ? results : [];
  var failed = null;
  for (var i = 0; i < list.length; i++) {
    if (list[i] && list[i].passed === false && (failed === null || stepOf(list[i]) < stepOf(failed))) {
      failed = list[i];
    }
  }
  if (failed) { return failMessage(failed, step); }
  // The checks tagged EXACTLY this step, per family.
  var exactRows = [];
  var exactOther = []; // { line } for one prompt / one function case
  var any = false;
  for (var j = 0; j < list.length; j++) {
    var res = list[j];
    if (!res || res.skipped || res.passed !== true) { continue; }
    any = true;
    if (res.family === 'prompt') {
      (res.prompts || []).forEach(function (p) {
        if (p.step === step) { exactOther.push({ line: promptPassLine(p.text) }); }
      });
    } else if (res.family === 'function') {
      (res.cases || []).forEach(function (c) {
        if (c.step === step) { exactOther.push({ line: String(c.line) }); }
      });
    } else {
      (res.rows || []).forEach(function (r) { if (r.rowStep === step) { exactRows.push(r); } });
    }
  }
  if (!any) { return null; }
  var lines = ['Step ' + step + ' works.'];
  var summary;
  if (exactOther.length === 0) {
    // Rows only: unchanged from step-checks (IeSYCMgQ).
    summary = passSummary(exactRows);
  } else {
    var total = exactRows.length + exactOther.length;
    summary = total === 1 ? exactOther[0].line : (total + ' checks passed.');
  }
  if (summary) { lines.push(summary); }
  return { tone: 'pass', lines: lines };
}
